import { useEffect, useState } from 'react'
import { Search, PackageOpen } from 'lucide-react'
import { fetchUnplacedRacks, type UnplacedRackItem } from '../../utils/layoutApi'

interface Props { dcCode: string; draftId?: number | null }

export function UnplacedRackQueue({ dcCode, draftId }: Props) {
  const [items, setItems] = useState<UnplacedRackItem[]>([])
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [search, setSearch] = useState('')
  const [type, setType] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    let active = true
    setLoading(true)
    fetchUnplacedRacks(dcCode, { search: search || undefined, type: type || undefined, draft_id: draftId ?? undefined })
      .then(next => { if (active) setItems(next) })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [dcCode, draftId, search, type])

  useEffect(() => {
    setSelectedIds(ids => ids.filter(id => items.some(item => item.id === id)))
  }, [items])

  return (
    <section style={{ borderBottom: '1px solid var(--border, #1a2744)', padding: '10px 10px 12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-secondary, #9ca3af)', fontSize: 11, fontWeight: 700 }}>
        <PackageOpen size={13} /> Инвентарь без места
        <span style={{ marginLeft: 'auto', color: 'var(--text-muted, #6b7280)', fontWeight: 500 }}>{items.length}</span>
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 8, border: '1px solid var(--border, #1a2744)', padding: '4px 6px', background: 'var(--bg-deep, #060a0f)' }}>
        <Search size={12} color="var(--text-muted, #6b7280)" />
        <input aria-label="Поиск неразмещённых стоек" value={search} onChange={event => setSearch(event.target.value)} placeholder="Поиск" style={{ minWidth: 0, width: '100%', background: 'transparent', border: 0, outline: 0, color: 'var(--text-primary, #f3f4f6)', fontSize: 11 }} />
      </label>
      <select aria-label="Фильтр типа стойки" value={type} onChange={event => setType(event.target.value)} style={{ width: '100%', marginTop: 6, padding: '4px 5px', background: 'var(--bg-deep, #060a0f)', border: '1px solid var(--border, #1a2744)', color: 'var(--text-secondary, #9ca3af)', fontSize: 11 }}>
        <option value="">Все типы</option>
        <option value="server_rack">Серверные</option>
        <option value="network_rack">Сетевые</option>
        <option value="storage">Хранилища</option>
      </select>
      <div style={{ display: 'grid', gap: 3, marginTop: 8, maxHeight: 180, overflowY: 'auto' }}>
        {!loading && items.length === 0 && <span style={{ color: 'var(--text-muted, #6b7280)', fontSize: 11 }}>Очередь пуста</span>}
        {items.map(item => (
          <div key={item.id} draggable onDragStart={event => {
            const ids = selectedIds.includes(item.id) ? selectedIds : [item.id]
            event.dataTransfer.setData('unplacedRackIds', ids.join(','))
            event.dataTransfer.effectAllowed = 'move'
          }} title="Перетащить стойку на карту" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 6px', border: '1px solid var(--border, #1a2744)', color: 'var(--text-primary, #f3f4f6)', fontSize: 11, cursor: 'grab', background: selectedIds.includes(item.id) ? 'var(--bg-hover, #182230)' : 'transparent' }}>
            <input aria-label={`Выбрать стойку ${item.label || item.id}`} type="checkbox" checked={selectedIds.includes(item.id)} onChange={() => setSelectedIds(ids => ids.includes(item.id) ? ids.filter(id => id !== item.id) : [...ids, item.id])} onClick={event => event.stopPropagation()} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label || `Rack #${item.id}`}</span>
            <span style={{ marginLeft: 'auto', color: 'var(--text-muted, #6b7280)', flexShrink: 0 }}>{item.u_slots ? `${item.u_slots}U` : ''}</span>
          </div>
        ))}
      </div>
    </section>
  )
}
